import { useCallback, useState } from 'react';
import { useEmbeddedAuthFetch } from '@/hooks/useEmbeddedAuthFetch';
import { API_ENDPOINTS } from '@/lib/api/endpoints';
import { portalLinkResponseSchema } from '@aica/api-types';
import { logError } from '@/lib/logger';

/**
 * Opens the full standalone portal from the embedded app — logged in.
 * Asks the SSO bridge for a one-time Clerk sign-in ticket URL, then opens it
 * in a new tab. On any failure, falls back to the plain /dashboard URL.
 */
export function useOpenPortal(): { openPortal: () => Promise<void>; isOpening: boolean } {
  const { authenticatedFetch } = useEmbeddedAuthFetch();
  const [isOpening, setIsOpening] = useState(false);

  const openPortal = useCallback(async () => {
    setIsOpening(true);
    try {
      const raw = await authenticatedFetch(API_ENDPOINTS.PORTAL_LINK, { method: 'POST' });
      const { url } = portalLinkResponseSchema.parse(raw);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      logError(
        'useOpenPortal: bridge failed, falling back to /dashboard',
        undefined,
        err instanceof Error ? err : new Error(String(err))
      );
      window.open(window.location.origin + '/dashboard', '_blank', 'noopener,noreferrer');
    } finally {
      setIsOpening(false);
    }
  }, [authenticatedFetch]);

  return { openPortal, isOpening };
}
